// DO Overview field-map + display/input helpers. The DivisionOrder analog of
// `pages/afe-catalog/structuring/identification-utils.ts`, but SIMPLER: unlike
// AFE Identification there is NO extension_data — every DO overview field is
// column-backed (routed via Document::DivisionOrder::FIELD_MAPPING) and returned
// in the response `fields` block. `OVERVIEW_FIELDS` is the single source of truth
// that drives BOTH the read-only Field/Value table and the edit SidePanel, so the
// label set / order never drift between them.
//
// Wire contract (KEEP):
//   * responses are camelCase (jbuilder key_format) — values read from
//     `fields[def.key]`.
//   * request bodies are snake_case — the edit panel submits each field flat
//     under `overview` keyed by `def.requestKey` (matches OverviewController's
//     overview_params permit list).

import type { OverviewFields } from './types-overview';

/** Rendered control type in the edit panel (the view always renders as text). */
export type OverviewFieldInput = 'text' | 'date' | 'dropdown';

// Status picklist for the edit-panel dropdown. Mirrors the BE vocabulary
// (Salesforce::DivisionOrder::STATUS_VALUES, which the edit is validated
// against), but ordered for the product's preferred display. 'In Progress' is
// the value the line-item rollup derives; the others are set here / by the sync.
export const STATUS_OPTIONS = [
  'Confirmed',
  'Pending',
  'In Dispute',
  'In Progress',
] as const;

export interface OverviewFieldDef {
  /** camelCase key inside the response `fields` block + the edit working copy. */
  key: keyof OverviewFields;
  /** Human label shown in the Field column + the edit-panel input label. */
  label: string;
  /** snake_case key submitted under `overview` (OverviewController#overview_params). */
  requestKey: string;
  input: OverviewFieldInput;
  /** Option set for `input: 'dropdown'` (strings or {label, value}). */
  options?: ReadonlyArray<string | { label: string; value: string }>;
  /**
   * Whether the field is rendered as an input in the edit panel. `false` fields
   * still show (read-only) in the Field/Value table. County is read-only here:
   * the BE renders it as the county NAME but writes `documents.county_id`, so a
   * free-text edit would submit a name where an id is expected — it needs a
   * county picker (name → id), tracked as a follow-up. The BE also sends
   * `fieldEditable` per field; the panel intersects that with this flag.
   */
  editable: boolean;
}

// The 12 mapped overview fields. Order matches the DO Overview read-only table
// (and the FIELD_MAPPING on Document::DivisionOrder). `requestKey` is the exact
// snake_case key OverviewController#overview_params permits.
// Field order matches the DO v2 mock (Document Type → … → Shared Drive Path).
// `county` is a live-only trailing field: it is a real column-backed
// DivisionOrder value the BE returns, so it's kept (read-only) even though the
// mock omits it — dropping it would hide real data, not just a mock nicety.
export const OVERVIEW_FIELDS: OverviewFieldDef[] = [
  {
    key: 'documentType',
    label: 'Document Type',
    requestKey: 'document_type',
    input: 'text',
    editable: true,
  },
  {
    key: 'doNumber',
    label: 'DO Number',
    requestKey: 'do_number',
    input: 'text',
    editable: true,
  },
  {
    key: 'status',
    label: 'Status',
    requestKey: 'status',
    input: 'dropdown',
    options: STATUS_OPTIONS,
    editable: true,
  },
  {
    // Division Order Source is always 'Provisioned' for structuring documents,
    // so it is NOT editable here — it stays read-only in the Field/Value table
    // (like owner/county). The BE also marks it non-editable in
    // Document::DivisionOrder::FIELD_MAPPING, so FE + BE intent stay aligned.
    key: 'divisionOrderSource',
    label: 'Division Order Source',
    requestKey: 'division_order_source',
    input: 'text',
    editable: false,
  },
  {
    // Owner is derived from the interest lines' per-line owner (DOAccount__c) and
    // is read-only here — a single Overview field can't edit N per-line owners
    // (they're edited in the Interest section). The BE also returns
    // fieldEditable.owner === false; this keeps FE + BE intent aligned (like county).
    key: 'owner',
    label: 'Owner',
    requestKey: 'owner',
    input: 'text',
    editable: false,
  },
  {
    key: 'dateReceived',
    label: 'Date Received',
    requestKey: 'date_received',
    input: 'date',
    editable: true,
  },
  {
    key: 'dateReturned',
    label: 'Date Returned',
    requestKey: 'date_returned',
    input: 'date',
    editable: true,
  },
  {
    key: 'dateConfirmed',
    label: 'Date Confirmed',
    requestKey: 'date_confirmed',
    input: 'date',
    editable: true,
  },
  {
    key: 'effectiveDate',
    label: 'Effective Date',
    requestKey: 'effective_date',
    input: 'date',
    editable: true,
  },
  {
    key: 'priority',
    label: 'Priority',
    requestKey: 'priority',
    input: 'text',
    editable: true,
  },
  {
    key: 'sharedDrivePath',
    label: 'Shared Drive Path',
    requestKey: 'shared_drive_path',
    input: 'text',
    editable: true,
  },
  {
    key: 'county',
    label: 'County',
    requestKey: 'county',
    input: 'text',
    editable: false,
  },
];

export const EDITABLE_OVERVIEW_FIELDS = OVERVIEW_FIELDS.filter(
  (f) => f.editable
);

const EM_DASH = '—';

/**
 * Display string for the read-only Field/Value table (blank → em-dash).
 * @param {OverviewFieldDef} def - The field definition.
 * @param {OverviewFields} fields - The response `fields` block.
 * @return {string} The display string.
 */
export const fieldDisplayValue = (
  def: OverviewFieldDef,
  fields: OverviewFields
): string => {
  const raw = fields[def.key];
  return raw === null || raw === undefined || raw === ''
    ? EM_DASH
    : String(raw);
};

/**
 * String form for an edit-panel input ('' instead of dash for empties).
 * @param {OverviewFieldDef} def - The field definition.
 * @param {OverviewFields} fields - The response `fields` block.
 * @return {string} The input value string ('' for empties).
 */
export const fieldInputValue = (
  def: OverviewFieldDef,
  fields: OverviewFields
): string => {
  const raw = fields[def.key];
  return raw === null || raw === undefined ? '' : String(raw);
};
